import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  TableContainer,
  HStack,
  Tooltip,
  InputGroup,
  InputLeftElement,
  Text,
  Icon,
  useToast,
  Center,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  VStack,
  Flex,
  Select,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  QuestionIcon,
  TriangleUpIcon,
  TriangleDownIcon
} from '@chakra-ui/icons';
import { MdEmail, MdSend, MdSync } from 'react-icons/md';
import { FaEnvelope, FaPaperPlane, FaExchangeAlt } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';
import EmailAuditLog from './EmailAuditLog';
import { emailTemplateService } from '../services/emailTemplateService';

// Test mode flag - set to true to prevent email sending
const TEST_MODE = true;

// Status transition matrix
const STATUS_TRANSITIONS = {
  REGULAR_ADMIN: {
    PENDING: ['REVOKED'],
    CONSENTED: ['REVOKED'],
    REVOKED: ['PENDING']
  },
  SUPERADMIN: {
    PENDING: ['REVOKED', 'CONSENTED'],
    CONSENTED: ['REVOKED', 'PENDING'],
    REVOKED: ['PENDING', 'CONSENTED']
  }
};

// Status color mapping
const getStatusColor = (status) => {
  switch (status) {
    case 'CONSENTED':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'REVOKED':
      return 'red';
    default:
      return 'gray';
  }
};

const ConsentManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [memberToEmail, setMemberToEmail] = useState(null);
  const [statusChangeData, setStatusChangeData] = useState(null);
  const [clubName, setClubName] = useState('');
  const toast = useToast();
  const emailHistoryModal = useDisclosure();
  const confirmEmailModal = useDisclosure();
  const statusChangeModal = useDisclosure();
  const confirmStatusChangeModal = useDisclosure();
  const helpModal = useDisclosure();
  const cancelRef = useRef();
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchMembers = async () => {
    try {
      const { data: membersData, error: membersError } = await supabase
        .from('texting_contacts')
        .select(`
          id,
          first_name,
          last_name,
          full_name,
          phone_number,
          email,
          consent_status,
          consent_history (
            consented_at,
            consented_by_name,
            status,
            revoked_at
          )
        `)
        .order('full_name');

      if (membersError) throw membersError;
      setMembers(membersData || []);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast({
        title: 'Error fetching members',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      if (user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        setCurrentUser(user);
        setIsSuperAdmin(profile.is_superadmin || false);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast({
        title: 'Error fetching user profile',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const handleRevokeConsent = async (memberId) => {
    try {
      // Start a transaction to update both tables
      const { error: consentError } = await supabase
        .from('consent_history')
        .insert([
          {
            member_id: memberId,
            status: 'REVOKED',
            consented_by_name: 'Admin',
            consent_message: 'Consent revoked by administrator',
            revoked_at: new Date().toISOString(),
          }
        ]);

      if (consentError) throw consentError;

      const { error: memberError } = await supabase
        .from('texting_contacts')
        .update({ consent_status: 'REVOKED' })
        .eq('id', memberId);

      if (memberError) throw memberError;

      toast({
        title: 'Consent revoked',
        description: 'Member consent has been revoked successfully.',
        status: 'success',
        duration: 5000,
      });

      fetchMembers();
    } catch (error) {
      console.error('Error revoking consent:', error);
      toast({
        title: 'Error revoking consent',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleViewEmailHistory = (member) => {
    setSelectedMember(member);
    emailHistoryModal.onOpen();
  };

  const handleResendWelcomeEmailClick = (member) => {
    console.log('Member data for email:', member); // Debug log
    setMemberToEmail(member);
    confirmEmailModal.onOpen();
  };

  const handleResendWelcomeEmail = async () => {
    if (!memberToEmail) return;
    
    try {
      setSendingEmail(true);
      
      // Fetch the welcome_email template
      const { data: template, error: templateError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('name', 'welcome_email')
        .maybeSingle();

      if (templateError) throw templateError;
      if (!template) throw new Error('Welcome email template not found');

      // Get club name from preferences
      const { data: preferences } = await supabase
        .from('club_preferences')
        .select('club_name')
        .single();

      const clubName = preferences?.club_name || 'Eden Prairie Noon Rotary';

      // Send the welcome email
      await emailTemplateService.sendTestEmail({
        template: {
          ...template,
          test_email: memberToEmail.email,
          variables: {
            firstName: memberToEmail.full_name.split(' ')[0],
            lastName: memberToEmail.full_name.split(' ').slice(1).join(' '),
            phone: memberToEmail.phone_number
          }
        },
        testName: memberToEmail.full_name,
        testPhone: memberToEmail.phone_number,
        clubName,
        supabase
      });

      toast({
        title: 'Welcome email sent',
        description: `Welcome email has been sent to ${memberToEmail.full_name} (${memberToEmail.email})`,
        status: 'success',
        duration: 3000,
      });

      confirmEmailModal.onClose();
    } catch (error) {
      console.error('Error sending welcome email:', error);
      toast({
        title: 'Error sending welcome email',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSendingEmail(false);
      setMemberToEmail(null);
    }
  };

  const handleStatusChangeClick = (member) => {
    setStatusChangeData({
      member,
      currentStatus: member.consent_status,
      newStatus: member.consent_status
    });
    statusChangeModal.onOpen();
  };

  const handleStatusChange = (newStatus) => {
    if (!statusChangeData) return;
    
    const userRole = isSuperAdmin ? 'SUPERADMIN' : 'REGULAR_ADMIN';
    const allowedTransitions = STATUS_TRANSITIONS[userRole][statusChangeData.currentStatus] || [];
    
    if (!allowedTransitions.includes(newStatus)) {
      toast({
        title: 'Invalid Status Change',
        description: `You cannot change status from ${statusChangeData.currentStatus} to ${newStatus}`,
        status: 'error',
        duration: 5000,
      });
      return;
    }

    setStatusChangeData(prev => ({
      ...prev,
      newStatus
    }));
  };

  const handleStatusChangeConfirm = async () => {
    if (!statusChangeData) return;
    const { currentStatus, newStatus } = statusChangeData;

    // Check if status is actually changing
    if (currentStatus === newStatus) {
      toast({
        title: 'No Change Needed',
        description: `Member's consent status is already set to ${newStatus}`,
        status: 'info',
        duration: 3000,
      });
      statusChangeModal.onClose();
      return;
    }

    // Show confirmation dialog for any status change
    confirmStatusChangeModal.onOpen();
  };

  const updateConsentStatus = async () => {
    if (!statusChangeData || !currentUser) return;
    const { member, currentStatus, newStatus } = statusChangeData;
    
    try {
      setSendingEmail(true);

      // 1. Update the consent status in texting_contacts
      const { error: updateError } = await supabase
        .from('texting_contacts')
        .update({ 
          consent_status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', member.id);

      if (updateError) throw updateError;

      // 2. Add record to consent_audit_log
      const now = new Date().toISOString();
      const auditRecord = {
        contact_id: member.id,
        action: 'ADMIN_UPDATE',
        previous_status: currentStatus,
        new_status: newStatus,
        changed_by: currentUser.id,
        changed_by_type: 'ADMIN',
        phone_number: member.phone_number,
        metadata: {
          reason: isSuperAdmin ? 'superadmin_override' : 'admin_update',
          timestamp: now,
          test_mode: TEST_MODE,
          original_recipient: member.email
        }
      };

      const { error: auditError } = await supabase
        .from('consent_audit_log')
        .insert(auditRecord);

      if (auditError) throw auditError;

      // Get the current user's email for test mode
      const testEmail = TEST_MODE ? currentUser.email : member.email;
      const emailRecipientName = TEST_MODE ? `${currentUser.user_metadata?.first_name || ''} ${currentUser.user_metadata?.last_name || ''}`.trim() : member.full_name;

      // 3. Handle email notifications
      if (newStatus === 'PENDING') {
        // Send welcome email for PENDING status
        const { data: template, error: templateError } = await supabase
          .from('email_templates')
          .select('*')
          .eq('name', 'welcome_email')
          .maybeSingle();

        if (templateError) throw templateError;
        if (!template) throw new Error('Welcome email template not found');

        await emailTemplateService.sendTestEmail({
          template: {
            ...template,
            test_email: testEmail,
            variables: {
              firstName: emailRecipientName.split(' ')[0],
              lastName: emailRecipientName.split(' ').slice(1).join(' '),
              phone: member.phone_number
            }
          },
          testName: emailRecipientName,
          testPhone: member.phone_number,
          clubName,
          supabase
        });
      } else if (newStatus === 'REVOKED') {
        // Send revocation email
        const { data: template, error: templateError } = await supabase
          .from('email_templates')
          .select('*')
          .eq('name', 'consent_revoked_email')
          .maybeSingle();

        if (templateError) throw templateError;
        if (!template) throw new Error('Consent revocation email template not found');

        await emailTemplateService.sendTestEmail({
          template: {
            ...template,
            test_email: testEmail,
            variables: {
              fullName: emailRecipientName,
              firstName: emailRecipientName.split(' ')[0],
              phone: {
                formattedShort: member.phone_number
              },
              clubName: clubName
            }
          },
          testName: emailRecipientName,
          testPhone: member.phone_number,
          clubName,
          supabase
        });
      } else if (newStatus === 'CONSENTED') {
        // Send confirmation email
        const { data: template, error: templateError } = await supabase
          .from('email_templates')
          .select('*')
          .eq('name', 'consent_confirmed_email')
          .maybeSingle();

        if (templateError) throw templateError;
        if (!template) throw new Error('Consent confirmation email template not found');

        await emailTemplateService.sendTestEmail({
          template: {
            ...template,
            test_email: testEmail,
            variables: {
              fullName: emailRecipientName,
              firstName: emailRecipientName.split(' ')[0],
              phone: {
                formattedShort: member.phone_number
              },
              clubName: clubName
            }
          },
          testName: emailRecipientName,
          testPhone: member.phone_number,
          clubName,
          supabase
        });
      }

      const toastDescription = TEST_MODE 
        ? `Member's consent status changed from ${currentStatus} to ${newStatus}. Test email sent to ${testEmail}`
        : `Member's consent status changed from ${currentStatus} to ${newStatus}`;

      toast({
        title: 'Status Updated',
        description: toastDescription,
        status: 'success',
        duration: 5000,
      });

      statusChangeModal.onClose();
      confirmStatusChangeModal.onClose();
      fetchMembers();
    } catch (error) {
      console.error('Error updating consent status:', error);
      let errorMessage = error.message;
      
      // Provide more specific error messages for template issues
      if (error.message.includes('template not found')) {
        errorMessage = `${error.message}. Please contact your administrator to set up the required email template.`;
      }
      
      toast({
        title: 'Error updating status',
        description: errorMessage,
        status: 'error',
        duration: 7000,
      });
    } finally {
      setSendingEmail(false);
      setStatusChangeData(null);
    }
  };

  const filteredMembers = members
    .filter(member => {
      const matchesSearch = (
        member.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.phone_number?.includes(searchQuery)
      );
      
      const matchesStatus = statusFilter === 'all' || member.consent_status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (!sortField) return 0;

      let aValue, bValue;

      switch (sortField) {
        case 'full_name':
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'phone_number':
          aValue = a.phone_number || '';
          bValue = b.phone_number || '';
          break;
        case 'consent_status':
          aValue = a.consent_status || '';
          bValue = b.consent_status || '';
          break;
        case 'last_updated':
          aValue = a.consent_history?.[0]?.consented_at || '0';
          bValue = b.consent_history?.[0]?.consented_at || '0';
          break;
        default:
          return 0;
      }

      const compareResult = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });

  const getStatusBadge = (status) => {
    return (
      <Badge colorScheme={getStatusColor(status)} fontSize="0.8em">
        {status}
      </Badge>
    );
  };

  const getLatestConsent = (consentHistory) => {
    if (!consentHistory?.length) return null;
    return consentHistory.reduce((latest, current) => {
      return !latest || new Date(current.consented_at) > new Date(latest.consented_at)
        ? current
        : latest;
    }, null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box p={4}>
      {TEST_MODE && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>Test Mode Active</AlertTitle>
            <AlertDescription>
              Status changes will be recorded and all notification emails will be sent to {currentUser?.email || 'your email address'} instead of the member's email address.
            </AlertDescription>
          </Box>
        </Alert>
      )}
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Manage Consent Process</Heading>
        <IconButton
          icon={<QuestionIcon />}
          variant="ghost"
          aria-label="Help"
          size="sm"
          onClick={helpModal.onOpen}
        />
      </HStack>

      <Stack spacing={4}>
        <HStack spacing={4}>
          <InputGroup flex={1}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search by name or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
          <Select
            w="200px"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="CONSENTED">Consented</option>
            <option value="PENDING">Pending</option>
            <option value="REVOKED">Revoked</option>
          </Select>
        </HStack>

        <TableContainer borderWidth="1px" borderRadius="md" shadow="sm">
          <Table variant="simple" size="xs">
            <Thead>
              <Tr>
                <Th cursor="pointer" onClick={() => handleSort('full_name')}>
                  <HStack spacing={0}>
                    <Text>NAME</Text>
                    <Stack spacing={-1} direction="column">
                      {(!sortField || sortField !== 'full_name') && (
                        <>
                          <Icon as={TriangleUpIcon} boxSize={2} color="gray.400" />
                          <Icon as={TriangleDownIcon} boxSize={2} color="gray.400" />
                        </>
                      )}
                      {sortField === 'full_name' && sortDirection === 'asc' && (
                        <Icon as={TriangleUpIcon} boxSize={2} color="black" />
                      )}
                      {sortField === 'full_name' && sortDirection === 'desc' && (
                        <Icon as={TriangleDownIcon} boxSize={2} color="black" />
                      )}
                    </Stack>
                  </HStack>
                </Th>
                <Th cursor="pointer" onClick={() => handleSort('phone_number')}>
                  <HStack spacing={1}>
                    <Text>PHONE</Text>
                    <Stack spacing={-1} direction="column">
                      {(!sortField || sortField !== 'phone_number') && (
                        <>
                          <Icon as={TriangleUpIcon} boxSize={2} color="gray.400" />
                          <Icon as={TriangleDownIcon} boxSize={2} color="gray.400" />
                        </>
                      )}
                      {sortField === 'phone_number' && sortDirection === 'asc' && (
                        <Icon as={TriangleUpIcon} boxSize={2} color="black" />
                      )}
                      {sortField === 'phone_number' && sortDirection === 'desc' && (
                        <Icon as={TriangleDownIcon} boxSize={2} color="black" />
                      )}
                    </Stack>
                  </HStack>
                </Th>
                <Th cursor="pointer" onClick={() => handleSort('consent_status')}>
                  <HStack spacing={1}>
                    <Text>STATUS</Text>
                    <Stack spacing={-1} direction="column">
                      {(!sortField || sortField !== 'consent_status') && (
                        <>
                          <Icon as={TriangleUpIcon} boxSize={2} color="gray.400" />
                          <Icon as={TriangleDownIcon} boxSize={2} color="gray.400" />
                        </>
                      )}
                      {sortField === 'consent_status' && sortDirection === 'asc' && (
                        <Icon as={TriangleUpIcon} boxSize={2} color="black" />
                      )}
                      {sortField === 'consent_status' && sortDirection === 'desc' && (
                        <Icon as={TriangleDownIcon} boxSize={2} color="black" />
                      )}
                    </Stack>
                  </HStack>
                </Th>
                <Th cursor="pointer" onClick={() => handleSort('last_updated')}>
                  <HStack spacing={1}>
                    <Text>LAST UPDATED</Text>
                    <Stack spacing={-1} direction="column">
                      {(!sortField || sortField !== 'last_updated') && (
                        <>
                          <Icon as={TriangleUpIcon} boxSize={2} color="gray.400" />
                          <Icon as={TriangleDownIcon} boxSize={2} color="gray.400" />
                        </>
                      )}
                      {sortField === 'last_updated' && sortDirection === 'asc' && (
                        <Icon as={TriangleUpIcon} boxSize={2} color="black" />
                      )}
                      {sortField === 'last_updated' && sortDirection === 'desc' && (
                        <Icon as={TriangleDownIcon} boxSize={2} color="black" />
                      )}
                    </Stack>
                  </HStack>
                </Th>
                <Th isNumeric>ACTIONS</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMembers.map((member) => (
                <Tr key={member.id}>
                  <Td>{member.full_name}</Td>
                  <Td>{member.phone_number}</Td>
                  <Td>
                    <Badge colorScheme={getStatusColor(member.consent_status)} fontSize="0.8em">
                      {member.consent_status}
                    </Badge>
                  </Td>
                  <Td>
                    {member.consent_history?.[0]?.consented_at
                      ? format(new Date(member.consent_history[0].consented_at), 'MMM d, yyyy h:mm a')
                      : 'Never'}
                  </Td>
                  <Td>
                    <HStack spacing={1} justifyContent="flex-end">
                      <Tooltip label="View email history">
                        <IconButton
                          icon={<MdEmail />}
                          onClick={() => handleViewEmailHistory(member)}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          aria-label="View Email History"
                        />
                      </Tooltip>
                      <Tooltip label="Resend welcome email">
                        <IconButton
                          icon={<MdSend />}
                          onClick={() => handleResendWelcomeEmailClick(member)}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          aria-label="Resend Welcome Email"
                        />
                      </Tooltip>
                      <Tooltip label="Change consent status">
                        <IconButton
                          icon={<MdSync />}
                          onClick={() => handleStatusChangeClick(member)}
                          size="sm"
                          colorScheme="purple"
                          variant="ghost"
                          aria-label="Change Consent Status"
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Stack>

      <Modal 
        isOpen={emailHistoryModal.isOpen} 
        onClose={emailHistoryModal.onClose} 
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxWidth="90vw">
          <ModalHeader>Email History - {selectedMember?.full_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <EmailAuditLog recipientId={selectedMember?.id} isModal={true} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={statusChangeModal.isOpen}
        onClose={statusChangeModal.onClose}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Consent Status</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text>
                Current Status: <Badge colorScheme={getStatusColor(statusChangeData?.currentStatus)}>{statusChangeData?.currentStatus}</Badge>
              </Text>
              <FormControl>
                <FormLabel>New Status</FormLabel>
                <Select
                  value={statusChangeData?.newStatus || ''}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value={statusChangeData?.currentStatus}>{statusChangeData?.currentStatus}</option>
                  {statusChangeData && STATUS_TRANSITIONS[isSuperAdmin ? 'SUPERADMIN' : 'REGULAR_ADMIN'][statusChangeData.currentStatus]?.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Select>
              </FormControl>
              {isSuperAdmin && (
                <Alert status="warning">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Superadmin Override</AlertTitle>
                    <AlertDescription>
                      You are making this change with superadmin privileges. This will bypass normal transition restrictions.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={statusChangeModal.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleStatusChangeConfirm}
              isDisabled={statusChangeData?.currentStatus === statusChangeData?.newStatus}
            >
              Change Status
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={confirmEmailModal.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={confirmEmailModal.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Resend Welcome Email
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text>
                  Are you sure you want to resend the welcome email to:
                </Text>
                <Box p={3} bg="gray.50" borderRadius="md">
                  <Text fontWeight="bold">{memberToEmail?.full_name}</Text>
                  <Text color="blue.600">{memberToEmail?.email}</Text>
                </Box>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={confirmEmailModal.onClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleResendWelcomeEmail} 
                ml={3}
                isLoading={sendingEmail}
              >
                Send Email
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={confirmStatusChangeModal.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={confirmStatusChangeModal.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Status Change
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={4}>
                <Text>
                  You are about to change {statusChangeData?.member?.full_name}'s consent status 
                  from <strong>{statusChangeData?.currentStatus}</strong> to <strong>{statusChangeData?.newStatus}</strong>.
                </Text>
                {statusChangeData?.newStatus === 'REVOKED' ? (
                  <Text>
                    This will:
                    <br />• Update their consent status to REVOKED
                    <br />• Set the revocation timestamp
                    <br />• Send them a consent revocation confirmation email
                    <br />• Record this change in the consent history
                  </Text>
                ) : statusChangeData?.newStatus === 'CONSENTED' ? (
                  <Text>
                    This will:
                    <br />• Update their consent status to CONSENTED
                    <br />• Set the consent timestamp
                    <br />• Send them a consent confirmation email
                    <br />• Record this change in the consent history
                  </Text>
                ) : statusChangeData?.newStatus === 'PENDING' ? (
                  <Text>
                    This will:
                    <br />• Update their consent status to PENDING
                    <br />• Clear any existing consent or revocation timestamps
                    {(statusChangeData?.currentStatus === 'REVOKED' || statusChangeData?.currentStatus === 'CONSENTED') && (
                      <>
                        <br />• Send them a new welcome email to allow them to consent again
                      </>
                    )}
                    <br />• Record this change in the consent history
                  </Text>
                ) : null}
                <Text>
                  Are you sure you want to proceed?
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={confirmStatusChangeModal.onClose}>
                Cancel
              </Button>
              <Button
                colorScheme={
                  statusChangeData?.newStatus === 'REVOKED' ? 'red' : 
                  statusChangeData?.newStatus === 'CONSENTED' ? 'blue' : 
                  'gray'
                }
                onClick={updateConsentStatus}
                ml={3}
                isLoading={sendingEmail}
              >
                Confirm Change
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal 
        isOpen={helpModal.isOpen} 
        onClose={helpModal.onClose} 
        size="lg"
        scrollBehavior="inside"
        motionPreset="none"
      >
        <ModalOverlay />
        <ModalContent maxH="600px">
          <ModalHeader borderBottomWidth="1px">Manage Consent Process Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Accordion allowMultiple defaultIndex={[0]}>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Overview
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  The Consent Management page allows you to track and manage member consent status for email and text communications.
                  You can view consent history, update consent status, and send consent-related communications to members.
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Features
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={2}>
                    <Text>• Search for members by name or phone number</Text>
                    <Text>• Filter members by consent status (All, Opted In, Opted Out, Pending)</Text>
                    <Text>• Sort members by any column by clicking the column header</Text>
                    <Text>• View detailed consent history for each member</Text>
                    <Text>• Update member consent status with proper documentation</Text>
                    <Text>• Send consent-related emails using templates</Text>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Actions
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={2}>
                    <Text>• View History: See detailed consent status change history</Text>
                    <Text>• Send Email: Send a consent-related email using a template</Text>
                    <Text>• Change Status: Update a member's consent status with documentation</Text>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Consent Statuses
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={2}>
                    <Text>• Opted In: Member has explicitly agreed to receive communications</Text>
                    <Text>• Opted Out: Member has explicitly declined to receive communications</Text>
                    <Text>• Pending: Member has not yet provided explicit consent</Text>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </ModalBody>

          <ModalFooter borderTopWidth="1px">
            <Button onClick={helpModal.onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ConsentManagement;
